<template>
  <v-app
    class="App"
    :class="{
      'App-galileo': $site === $const.sites.galileo,
      'App-nikon': $site === $const.sites.nikon,
      'App-nikon-it': $site === $const.sites['nikon-it']
    }"
  >
    <v-main v-if="$route.meta.public"> <router-view /> </v-main>

    <template v-else>
      <Loader v-if="!loadedData" overlay />
      <template v-else>
        <Sidebar v-if="displaySidebar" />

        <v-main class="App-view">
          <Topbar v-if="displayTopBar" />
          <router-view />
        </v-main>
      </template>
    </template>

    <ConfirmModal
      :confirm="modal.confirmModal.confirm"
      :content="modal.confirmModal.content"
      :display="modal.confirmModal.display"
      :title="modal.confirmModal.title"
    />
  </v-app>
</template>

<script>
import gql from 'graphql-tag'
import { mapState } from 'vuex'
import { localize } from 'vee-validate'

import Loader from '@/components/lib/Loader'
import Sidebar from '@/components/layout/Sidebar'
import ConfirmModal from '@/components/modals/ConfirmModal'
import Topbar from '@/components/layout/Topbar'
import auth from '@/services/auth'
import { getLangWithUrl } from '@/services/lang'
import { initVueGtm } from '@/services/gtm'
import CONST from '@/const/shared'

const GET_MY_USER = gql`
  query getMyUser {
    myUser {
      admin
      ecpId
      email
      userUuid
      role
      shops {
        shopUuid
        address1
        address2
        city
        country
        displayName
        email
        enabled
        iCalLink
        isOnboarded
        logo
        maxParallelAppointments {
          ${CONST.dayOfWeek.FRIDAY}
          ${CONST.dayOfWeek.MONDAY}
          ${CONST.dayOfWeek.SATURDAY}
          ${CONST.dayOfWeek.SUNDAY}
          ${CONST.dayOfWeek.THURSDAY}
          ${CONST.dayOfWeek.TUESDAY}
          ${CONST.dayOfWeek.WEDNESDAY}
        }
        notificationACKUpdatedAt
        openings {
          dayOfWeek
          beginHour
          endHour
          shopUuid
        }
        phoneNumber
        postalCode
        services {
          duration
          enabled
          icon
          name
          openings {
            beginHour
            dayOfWeek
            endHour
          }
          parallelAppointment
          price
          serviceType
          serviceUuid
        }
      }
    }
  }
`

export default {
  name: 'App',

  components: { ConfirmModal, Sidebar, Loader, Topbar },

  data() {
    return {
      myUser: null
    }
  },

  computed: {
    ...mapState(['modal']),
    displaySidebar() {
      return this.$route.name !== 'Onboarding' && this.$route.name !== 'AdminPanel'
    },
    displayTopBar() {
      return this.$route.name !== 'Onboarding' && this.$route.name !== 'AdminPanel'
    },
    loadedData() {
      return Boolean(this.myUser)
    }
  },

  apollo: {
    myUser: {
      query: GET_MY_USER,
      skip() {
        return Boolean(this.$route.meta.public) || this.$route.matched.length === 0
      },
      result({ data }) {
        if (!data) return
        if (!this.$store.state.skipSetMyUser) {
          this.$store.commit('SET_MY_USER', data.myUser)
        } else {
          this.$store.commit('SET_SKIP_MY_USER', false)
        }
        if ([this.$const.userRoles.ADMIN, this.$const.userRoles.COUNTRY_MANAGER].includes(data.myUser.role)) {
          this.$router.replace({ name: 'AdminPanel' })
          return
        }
        const isOnboarding =
          data.myUser.shops[0].services.every(service => service.openings.length === 0) &&
          !data.myUser.shops[0].isOnboarded

        if (isOnboarding && this.$route.name !== 'Onboarding') {
          this.$router.replace({ name: 'Onboarding' })
        }
      },
      async error(err) {
        if (err.message.includes('isAuthenticated')) {
          await auth.logout()
          this.$store.commit('RESET_STORE')
          await this.$router.replace({ name: 'Login' })
        }
      }
    }
  },

  mounted() {
    if (this.$site === this.$const.sites.galileo) {
      const favicon = document.getElementById('favicon')
      favicon.href = '/galileo-favicon.ico'
    }
    if ([this.$const.sites['nikon-it'], this.$const.sites.nikon].includes(this.$site)) {
      const favicon = document.getElementById('favicon')
      favicon.href = '/nikon-favicon.png'
    }
  },

  updated() {
    const lang = getLangWithUrl(this.$route.params.lang)
    this.$vuetify.lang.current = lang.vuetify
    localize(lang.veeValidate, require(`vee-validate/dist/locale/${lang.veeValidate}`))

    this.senGtmLoadPage()
  },

  methods: {
    senGtmLoadPage() {
      if (
        !this.$route.meta.public &&
        (!this.$get(this.myUser, 'ecpId') || !this.$get(this.$store, 'getters.currentShop.country'))
      ) {
        return
      }

      if (this.$store.getters.currentShop && this.$store.getters.currentShop.country) {
        if (!this.$gtm) {
          initVueGtm(this.$store.getters.currentShop.country)
        }

        this.$gtm.trackEvent({
          ContentGroup: this.$route.name,
          essilor_client_id: this.$get(this.myUser, 'ecpId', ''),
          practitioner_id: this.$get(this.myUser, 'ecpId', ''),
          shopUuid: this.$get(this.$store, 'getters.currentShop.shopUuid', ''),
          country: this.$get(this.$store, 'getters.currentShop.country', ''),
          language: this.$route.params.lang
        })
      }
    }
  }
}
</script>

<style lang="scss">
@import '~vue-wysiwyg/dist/vueWysiwyg.css';

.App-view {
  background-color: var(--v-secondary-lighten1);
  color: var(--v-secondary-base);
}

.App-nikon .primaryBtn,
.App-nikon-it .primaryBtn {
  background-color: var(--v-primaryBtn-base) !important;
  border-color: var(--v-primaryBtn-base) !important;
  font-weight: bold !important;
  color: var(--v-secondary-base) !important;
}

.App-nikon .primary,
.App-nikon-it .primary {
  background-color: var(--v-primaryBtn-base) !important;
  border-color: var(--v-primaryBtn-base) !important;
  font-weight: bold !important;
  color: var(--v-secondary-base) !important;
}

.App-nikon .v-picker,
.App-nikon-it .v-picker {
  .v-picker__title {
    background-color: var(--v-primaryCalendar-base) !important;
    color: '#FFFFFF' !important;
  }
  .v-btn--active {
    color: '#FFFFFF' !important;
    background-color: var(--v-primaryCalendar-base) !important;
  }
  .v-btn--outlined {
    color: var(--v-secondary-base) !important;
  }
}
</style>
